import { Component, OnInit } from '@angular/core';
import { combineLatest, filter, switchMap, take, tap, takeWhile } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { AuthService } from '@core/services/auth.service';
import { SocketService } from '@core/services/socket.service';
import { HotjarService } from '@core/services/hotjar.service';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { selectAuthUser } from '@state/auth/auth.selectors';
import {
  setAuthModal,
  updateUserConfirmedAtStatuses,
} from '@state/auth/auth.actions';

@UntilDestroy()
@Component({
  selector: 'app-auth-verify-identity',
  templateUrl: './verify-identity.component.html',
  styleUrls: ['./verify-identity.component.scss'],
})
export class VerifyIdentityComponent implements OnInit {
  protected phoneNumberFormOpen = true;
  protected phoneNumberConfirmedAt: string | undefined | null;
  protected emailConfirmedAt: string | undefined | null;
  protected userEmail = '';

  constructor(
    private authService: AuthService,
    private store: Store<AppState>,
    private socketService: SocketService,
    private hotjarService: HotjarService
  ) {}

  ngOnInit(): void {
    this.hotjarService.callEvent('start_verify_identity');

    const user$ = this.store.select(selectAuthUser).pipe(
      tap((user) => {
        this.phoneNumberConfirmedAt = user?.phoneNumberConfirmedAt;
        this.emailConfirmedAt = user?.emailConfirmedAt;
        this.userEmail = user?.email || '';
        this.verifyIfConfirmed();
      }),
      filter((u) => !!u),
      take(2)
    );

    const ready$ = this.socketService.ready$.pipe(
      filter((ready) => ready),
      tap(() =>
        console.log('Socket ready, proceeding to user status subscribe...')
      ),
      take(1)
    );

    combineLatest([user$, ready$])
      .pipe(
        // If both are already confirmed by the time we get here, skip opening the WS subscription.
        filter(
          () => !(!!this.phoneNumberConfirmedAt && !!this.emailConfirmedAt)
        ),
        switchMap(() => this.authService.subscribeToUserUpdate()),
        // Keep listening until BOTH confirmations are present; include the final emission, then complete.
        takeWhile(
          (status) =>
            !(
              status.updated.phoneNumberConfirmedAt &&
              status.updated.emailConfirmedAt
            ),
          true
        ),
        untilDestroyed(this)
      )
      .subscribe({
        next: (status) => {
          console.log('Received user verification status update:', status);
          this.phoneNumberConfirmedAt = status.updated.phoneNumberConfirmedAt;
          this.emailConfirmedAt = status.updated.emailConfirmedAt;
          this.store.dispatch(
            updateUserConfirmedAtStatuses({
              emailConfirmedAt: this.emailConfirmedAt,
              phoneNumberConfirmedAt: this.phoneNumberConfirmedAt,
            })
          );
          this.verifyIfConfirmed();
        },
        error: (error) => {
          console.error('Error fetching user verification status:', error);
        },
      });
  }

  toggleOpen(value: boolean): void {
    this.phoneNumberFormOpen = value;
  }

  verifyIfConfirmed() {
    if (this.phoneNumberConfirmedAt && this.emailConfirmedAt) {
      this.store.dispatch(setAuthModal({ authModal: 'account-setup' }));
    }
  }
}
